import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EstablishmentContext = createContext(null);

export function EstablishmentProvider({ children }) {
  const { token, user } = useAuth();
  const [establishments, setEstablishments] = useState([]);
  const [currentEstablishment, setCurrentEstablishment] = useState(null);
  const [hasGroup, setHasGroup] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState('owner');

  const fetchEstablishments = useCallback(async () => {
    if (!token || user?.role !== 'Restaurant') {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/multisite/establishments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEstablishments(response.data.establishments || []);
      setHasGroup(response.data.has_group || false);
      
      // Get current establishment
      const currentRes = await axios.get(`${API}/multisite/current-establishment`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCurrentEstablishment(currentRes.data.current_establishment);
      setUserRole(currentRes.data.role || 'owner');
    } catch (err) {
      console.error('Error fetching establishments:', err);
      // If no group, use single restaurant
      if (user?.restaurant_id) {
        setEstablishments([{
          id: user.restaurant_id,
          name: user.restaurant_name || 'Mon Restaurant',
          is_main: true
        }]);
        setCurrentEstablishment({
          id: user.restaurant_id,
          name: user.restaurant_name || 'Mon Restaurant'
        });
      }
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  useEffect(() => {
    fetchEstablishments();
  }, [fetchEstablishments]);

  const switchEstablishment = async (establishmentId) => {
    if (!token) return;

    try {
      const response = await axios.post(
        `${API}/multisite/switch-establishment/${establishmentId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCurrentEstablishment(response.data.current_establishment);
      
      // Refresh the page to reload data for new establishment
      window.location.reload();
    } catch (err) {
      console.error('Error switching establishment:', err);
      throw err;
    }
  };

  const refreshEstablishments = () => {
    fetchEstablishments();
  };

  const value = {
    establishments,
    currentEstablishment,
    hasGroup,
    loading,
    userRole,
    switchEstablishment,
    refreshEstablishments,
    isOwner: userRole === 'owner',
    isManager: userRole === 'manager',
    isEmployee: userRole === 'employee',
    canManageEstablishments: userRole === 'owner',
    canManageSubAccounts: userRole === 'owner'
  };

  return (
    <EstablishmentContext.Provider value={value}>
      {children}
    </EstablishmentContext.Provider>
  );
}

export function useEstablishment() {
  const context = useContext(EstablishmentContext);
  if (!context) {
    throw new Error('useEstablishment must be used within an EstablishmentProvider');
  }
  return context;
}
